import React, { useEffect } from "react";
import { connect } from "react-redux";

import { EventInput } from "@fullcalendar/common";

import { Activity, EntityType } from "@mapmycustomers/shared/types/entity";

import CalendarView from "@app/component/view/CalendarView/CalendarView";
import CalendarViewState from "@app/component/view/CalendarView/types/CalendarViewState";
import { RootState } from "@app/store/rootReducer";
import { subscribe, unsubscribe } from "@app/store/uiSync/actions";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import activityFieldModel from "@app/util/fieldModel/ActivityFieldModel";

import { smartPlannerViewAnalytics } from "../../activityTableAnalytics";
import ViewMode from "../../enum/ViewMode";
import Subheader from "../Subheader";

import Calendar from "./components/Calendar";
import CalendarLayout from "./components/CalendarLayout";
import CalendarToolbar from "./components/CalendarToolbar";
import Sidebar from "./components/Sidebar";
import {
  getCalendarEvents,
  getCalendarViewState,
  getCalendarViewTotalFilteredRecordsCount,
  getCalendarViewTotalRecordsCount,
  isCalendarViewLoading,
} from "./store";
import { fetchCalendarActivities, initializeCalendarView } from "./store/actions";

interface Props {
  events: EventInput[];
  fetcher: typeof fetchCalendarActivities.request;
  initialize: () => void;
  loading?: boolean;
  subscribe: typeof subscribe;
  totalFilteredRecords: number;
  totalRecords: number;
  unsubscribe: typeof unsubscribe;
  viewState: CalendarViewState;
}

const ActivityCalendarPage: React.FC<Props> = ({
  events,
  fetcher,
  initialize,
  loading,
  subscribe,
  totalFilteredRecords,
  totalRecords,
  unsubscribe,
  viewState,
}) => {
  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    const listener = {
      onAdd: () => fetcher({ viewState: {} }),
      onDelete: () => fetcher({ viewState: {} }),
      onUpdate: () => fetcher({ viewState: {} }),
    };
    subscribe({ entityTypes: [EntityType.ACTIVITY], listener });
    return () => {
      unsubscribe({ entityTypes: [EntityType.ACTIVITY], listener });
    };
  }, [fetcher, subscribe, unsubscribe]);

  return (
    <AnalyticsContext.Provider value={smartPlannerViewAnalytics}>
      <CalendarView<Activity>
        entityType={EntityType.ACTIVITY}
        fetcher={fetcher}
        fieldModel={activityFieldModel}
        viewState={viewState}
      >
        <Subheader
          totalFilteredRecords={totalFilteredRecords}
          totalRecords={totalRecords}
          viewMode={ViewMode.CALENDAR}
        />
        <CalendarLayout loading={loading} sidebar={<Sidebar />}>
          <CalendarToolbar />
          <Calendar events={events} />
        </CalendarLayout>
      </CalendarView>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  events: getCalendarEvents(state),
  loading: isCalendarViewLoading(state),
  totalFilteredRecords: getCalendarViewTotalFilteredRecordsCount(state),
  totalRecords: getCalendarViewTotalRecordsCount(state),
  viewState: getCalendarViewState(state),
});

const mapDispatchToProps = {
  fetcher: fetchCalendarActivities.request,
  initialize: initializeCalendarView.request,
  subscribe,
  unsubscribe,
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivityCalendarPage);
